/**
 * GameEventHandler — WebSocket 事件處理器
 *
 * 集中處理伺服器推送的遊戲事件，將 WebSocket 訊息轉為 store 操作。
 * 在連線模式下，遊戲狀態完全由伺服器事件驅動。
 */

import { useUIStore } from '../store/uiStore';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import {
  getWSClient,
  type WSMessage,
  type MatchFoundPayload,
  type GameEventPayload,
  type StateSyncPayload,
  type WSErrorPayload,
} from './NetworkManager';
import { getGlobalTimer, resetGlobalTimer } from '../core/systems/TimerManager';
import { getLogger } from '../utils/logger';

const log = getLogger('GameEventHandler');

/** 連線對戰相關的狀態（不放在 Zustand，因為是臨時控制流） */
interface OnlineGameContext {
  gameId: string | null;
  playerSide: 'player1' | 'player2' | null;
  opponentName: string | null;
}

const onlineContext: OnlineGameContext = {
  gameId: null,
  playerSide: null,
  opponentName: null,
};

/** 配對狀態回調 */
type MatchStateCallback = (state: 'searching' | 'found' | 'timeout' | 'cancelled') => void;
let matchStateCallback: MatchStateCallback | null = null;

/** 對手斷線/重連回調 */
type OpponentStateCallback = (state: 'disconnected' | 'reconnected', timeoutSeconds?: number) => void;
let opponentStateCallback: OpponentStateCallback | null = null;

/** 設定配對狀態回調 */
export function onMatchStateChange(cb: MatchStateCallback | null): void {
  matchStateCallback = cb;
}

/** 設定對手狀態回調 */
export function onOpponentStateChange(cb: OpponentStateCallback | null): void {
  opponentStateCallback = cb;
}

/** 取得當前連線對戰上下文 */
export function getOnlineContext(): Readonly<OnlineGameContext> {
  return onlineContext;
}

/** 清除連線對戰上下文 */
export function clearOnlineContext(): void {
  onlineContext.gameId = null;
  onlineContext.playerSide = null;
  onlineContext.opponentName = null;
}

// ============================================================
// WebSocket 訊息處理
// ============================================================

/** 是否已註冊監聽器 */
let listenerRegistered = false;
let unsubscribe: (() => void) | null = null;

/** 啟動事件監聽（在登入並建立 WS 連線後呼叫） */
export function startListening(): void {
  if (listenerRegistered) return;

  const wsClient = getWSClient();
  unsubscribe = wsClient.onMessage(handleWSMessage);
  listenerRegistered = true;
  log.debug('已啟動事件監聽');
}

/** 停止事件監聽 */
export function stopListening(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  listenerRegistered = false;
  clearOnlineContext();
  log.debug('已停止事件監聽');
}

/** 主訊息分發器 */
function handleWSMessage(msg: WSMessage): void {
  switch (msg.type) {
    case 'match_found':
      handleMatchFound(msg.payload as MatchFoundPayload);
      break;
    case 'match_timeout':
      handleMatchTimeout();
      break;
    case 'state_sync':
      handleStateSync(msg.payload as StateSyncPayload);
      break;
    case 'event':
      handleGameEvent(msg.payload as GameEventPayload);
      break;
    case 'error':
      handleError(msg.payload as WSErrorPayload);
      break;
    default:
      // ping/pong 由 WebSocketClient 內部處理
      break;
  }
}

// ============================================================
// 配對事件
// ============================================================

function handleMatchFound(payload: MatchFoundPayload): void {
  log.debug('配對成功:', payload);

  onlineContext.gameId = payload.game_id;
  onlineContext.playerSide = payload.side;
  onlineContext.opponentName = payload.opponent_name;

  matchStateCallback?.('found');

  // 注意：game_start 事件會在雙方都連上 WS 後由伺服器自動發送
}

function handleMatchTimeout(): void {
  log.debug('配對超時');
  matchStateCallback?.('timeout');
}

// ============================================================
// 遊戲事件
// ============================================================

function handleGameEvent(payload: GameEventPayload): void {
  const eventType = payload.type;
  log.debug(`遊戲事件: ${eventType}`, payload);

  switch (eventType) {
    case 'game_start':
      handleGameStart();
      break;
    case 'game_over':
      handleGameEnd(payload);
      break;
    case 'time_sync':
      handleTimeSync(payload);
      break;
    case 'timer_expired':
      handleTimerExpired();
      break;
    case 'pending_choice':
      handlePendingChoice(payload);
      break;
    case 'choice_submitted':
    case 'choice_cancelled':
      handleChoiceResolved(payload);
      break;
    case 'opponent_disconnected':
      handleOpponentDisconnected(payload);
      break;
    case 'opponent_reconnected':
      handleOpponentReconnected();
      break;
    default:
      // 其他遊戲事件（phase_change, card_drawn 等）由 gameStore 處理
      handleGenericGameEvent(payload);
      break;
  }
}

/** 快取 state_sync 資料（在 game_start 事件之前到達） */
let pendingStateSync: StateSyncPayload | null = null;

function handleStateSync(payload: StateSyncPayload): void {
  log.debug('收到 state_sync:', payload.game_id);

  // 若遊戲已啟動（重連場景），直接更新狀態
  const gameStore = useGameStore.getState();
  if (gameStore.onlineGameId) {
    gameStore.updateOnlineGameState(payload.state);
    return;
  }

  // 首次連線：快取等待 game_start 事件
  pendingStateSync = payload;
}

function handleGameStart(): void {
  log.debug('遊戲開始');

  const gameId = pendingStateSync?.game_id || onlineContext.gameId || '';
  const playerSide = onlineContext.playerSide || 'player1';
  const initialState = pendingStateSync?.state;

  // 清除快取
  pendingStateSync = null;

  // 通知 gameStore 開始連線對戰
  const gameStore = useGameStore.getState();
  gameStore.startOnlineGame(gameId, playerSide, initialState);

  // 初始化遊戲計時器
  const timer = getGlobalTimer();
  const state = gameStore.gameState;
  const timeLimit = state?.gameTimeLimit || 1800;
  const startedAt = state?.gameStartedAt;
  let elapsed = 0;
  if (startedAt) {
    elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  }
  timer.startGameTimer(timeLimit, elapsed);

  // 導航到遊戲畫面
  const uiStore = useUIStore.getState();
  uiStore.setGameMode('online');
  uiStore.navigateTo('game');
}

function handleGameEnd(payload: GameEventPayload): void {
  log.debug('遊戲結束:', payload);

  const data = payload.data || {};
  const gameStore = useGameStore.getState();
  const authStore = useAuthStore.getState();
  const myPlayerId = authStore.player?.id;
  const winnerId = (data.winner_id as string) || '';
  const winCondition = (data.win_condition as string) || 'unknown';
  const isWinner = winnerId === myPlayerId;

  // 停止計時器
  resetGlobalTimer();

  // 通知 gameStore 遊戲結束
  gameStore.handleOnlineGameEnd(winnerId, winCondition, isWinner);

  // 導航到勝利畫面
  const uiStore = useUIStore.getState();
  uiStore.navigateTo('victory');

  // 清除對戰上下文
  clearOnlineContext();
}

function handleTimeSync(payload: GameEventPayload): void {
  const remainingSeconds = payload.data?.remaining_seconds as number | undefined;
  if (remainingSeconds !== undefined) {
    const timer = getGlobalTimer();
    timer.syncTime(remainingSeconds);
    log.debug('時間同步:', remainingSeconds, '秒');
  }
}

function handleTimerExpired(): void {
  log.debug('遊戲計時器到期');
  const timer = getGlobalTimer();
  timer.syncTime(0);
}

function handleOpponentDisconnected(payload: GameEventPayload): void {
  log.debug('對手斷線');
  const timeoutSeconds = payload.data?.timeout_seconds as number | undefined;
  opponentStateCallback?.('disconnected', timeoutSeconds);
}

function handleOpponentReconnected(): void {
  log.debug('對手重連');
  opponentStateCallback?.('reconnected');
}

function handleGenericGameEvent(payload: GameEventPayload): void {
  // 通用事件處理（伺服器推送的遊戲狀態更新）
  const gameStore = useGameStore.getState();
  gameStore.handleOnlineEvent({
    event: payload.type,
    data: payload.data,
  });
}

// ============================================================
// 選擇系統事件
// ============================================================

function handlePendingChoice(payload: GameEventPayload): void {
  const data = payload.data || {};
  log.debug('收到 pending_choice:', data);

  const gameStore = useGameStore.getState();

  // Map server pending_choice data to frontend PendingChoice format
  const pendingChoice = {
    id: (data.choice_id as string) || '',
    type: mapChoiceType(data.type as string),
    sourceCardId: (data.source_card_id as string) || '',
    sourceCardName: '',
    effectText: (data.effect_text as string) || '',
    playerId: (data.player_id as string) || '',
    filter: {
      side: mapFilterSide(data.filter_side as string),
      zone: mapFilterZone(data.filter_zone as string),
    },
    minCount: (data.min_count as number) || 1,
    maxCount: (data.max_count as number) || 1,
    optional: (data.optional as boolean) || false,
    createdAt: Date.now(),
  };

  // Update the store to show the choice overlay
  gameStore.setPendingChoiceFromServer(pendingChoice);
}

function handleChoiceResolved(payload: GameEventPayload): void {
  log.debug('選擇已完成:', payload.type, payload.data);
  // State will be updated via state_sync; no additional action needed.
}

/** Map server choice type string to frontend ChoiceType */
function mapChoiceType(serverType: string): 'target' | 'card' | 'option' | 'position' | 'number' {
  switch (serverType) {
    case 'target': return 'target';
    case 'card': return 'card';
    case 'option': return 'option';
    case 'position': return 'position';
    default: return 'target';
  }
}

/** Map server filter side to frontend filter side */
function mapFilterSide(serverSide: string): 'self' | 'enemy' | 'both' | 'any' {
  switch (serverSide) {
    case 'self': return 'self';
    case 'enemy': return 'enemy';
    case 'both': return 'both';
    default: return 'both';
  }
}

/** Map server filter zone to frontend filter zone */
function mapFilterZone(serverZone: string): 'battlefield' | 'hand' | 'graveyard' | 'deck' | 'forest' {
  switch (serverZone) {
    case 'battlefield': return 'battlefield';
    case 'hand': return 'hand';
    case 'graveyard': return 'graveyard';
    case 'deck': return 'deck';
    case 'forest': return 'forest';
    default: return 'battlefield';
  }
}

// ============================================================
// 錯誤處理
// ============================================================

function handleError(payload: WSErrorPayload): void {
  log.error(`伺服器錯誤: [${payload.code}] ${payload.message}`);
}

// ============================================================
// 指令發送（對外介面）
// ============================================================

/** 發送遊戲指令到伺服器 */
export function sendGameCommand(commandType: string, data?: unknown): void {
  const gameId = onlineContext.gameId;
  if (!gameId) {
    log.error('無法發送指令：沒有進行中的對局');
    return;
  }

  const wsClient = getWSClient();
  wsClient.sendCommand(gameId, commandType, data);
}

// Debug: expose sendGameCommand and online context for testing
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__SEND_CMD__ = sendGameCommand;
  (window as unknown as Record<string, unknown>).__ONLINE_CTX__ = onlineContext;
}
